import React, { useState } from 'react';
import Cf7FormWrapper from '@/components/cf7-form-wrapper';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import Image from 'next/image'
import Link from 'next/link'
type FormState = {
    'your-name': string;
    'your-phone': string;
    'your-email': string;
    'your-business': string;
    'enquiry-type': string[];
  };
  
  function Form({ handler, isLoading, isSent, hasError, captchaKey }: any) {
    const router = useRouter();
    const initialFormState: FormState = {
      'your-name': '',
      'your-phone': '',
      'your-email': '',
      'your-business': '',
      'enquiry-type': [],
    };
  
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [showEmptyError, setShowEmptyError] = useState(false);
    const [gReCaptchaToken, setGReCaptchaToken] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
  
    const handleFieldChange = (
      field: keyof FormState,
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      setFormState({
        ...formState,
        [field]: e.target.value,
      });
    };
  
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const checked = e.target.checked;
      setFormState(prevState => {
        const newEnquiryType = checked
          ? [...prevState['enquiry-type'], value]
          : prevState['enquiry-type'].filter(item => item !== value);
        return {
          ...prevState,
          'enquiry-type': newEnquiryType,
        };
      });
    };
  
    const handleCaptchaChange = (token: string | null) => {
      setGReCaptchaToken(token);
    };
  
    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
  
      const isEmpty = Object.values(formState).some(value => value === '' || value.length === 0);
  
      if (isEmpty) {
        setShowEmptyError(true);
        return;
      }
  
      // Reset the error flag and submit the form
      setShowEmptyError(false);
      handler(e, formState);
  
      // Reset form fields
      if (formRef.current) {
        formRef.current.reset();
        setFormState(initialFormState);
        setGReCaptchaToken(null);
  
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
  
      setSubmitSuccess(true);
      await router.push('/thank-you');
    };
  
    return (
      <form ref={formRef} onSubmit={handleFormSubmit} className='sc_form'>
        {isSent && submitSuccess && <div className="alert alert-success" role="alert">Thank you for your submission!</div>}
        {hasError && <div className="alert alert-danger" role="alert">Error: {hasError}</div>}
        
        <div className="row">
          <div className="col-lg-6">
            <div className="input_wraper">
              <label htmlFor="name">Name*</label>
              <input
                id='name'
                type="text"
                name="your-name"
                onChange={(e) => handleFieldChange("your-name", e)}
              />
              {showEmptyError && formState['your-name'] === '' && <p className='error'>Name is required.</p>}
            </div>
          </div>
          <div className="col-lg-6">
            <div className="input_wraper">
              <label htmlFor="phone">Phone*</label>
              <input
                id='phone'
                type="tel"
                pattern="[0-9]{11}"
                name="your-phone"
                onChange={(e) => handleFieldChange("your-phone", e)}
              />
              {showEmptyError && formState['your-phone'] === '' && <p className='error'>Phone is required.</p>}
            </div>
          </div>
          <div className="col-lg-6">
            <div className="input_wraper">
              <label htmlFor="email">Email*</label>
              <input
                id='email'
                type="email"
                name="your-email"
                onChange={(e) => handleFieldChange("your-email", e)}
              />
              {showEmptyError && formState['your-email'] === '' && <p className='error'>Email is required.</p>}
            </div>
          </div>
          <div className="col-lg-6">
            <div className="input_wraper">
              <label htmlFor="business">Name of business*</label>
              <input
                id='business'
                type="text"
                name="your-business"
                onChange={(e) => handleFieldChange("your-business", e)}
              />
              {showEmptyError && formState['your-business'] === '' && <p className='error'>Business is required.</p>}
            </div>
          </div>
          <div className="col-lg-12">
            <div className="input_wraper">
              <label htmlFor="enquirytype">What are you interested in*</label>
              <div className="checkbox_wraper">
                <label htmlFor="website">
                  <input 
                    id='website' 
                    name="enquiry-type[]" 
                    type="checkbox" 
                    value="Website" 
                    onChange={handleCheckboxChange} 
                  />
                  <span>Website</span>
                </label>
                <label htmlFor="design">
                  <input 
                    id='design' 
                    name="enquiry-type[]" 
                    type="checkbox" 
                    value="Design" 
                    onChange={handleCheckboxChange} 
                  />
                  <span>Design</span>
                </label>
                <label htmlFor="marketing">
                  <input 
                    id='marketing' 
                    name="enquiry-type[]" 
                    type="checkbox" 
                    value="Marketing" 
                    onChange={handleCheckboxChange} 
                  />
                  <span>Marketing</span>
                </label>
                <label htmlFor="leadgeneration">
                  <input 
                    id='leadgeneration' 
                    name="enquiry-type[]" 
                    type="checkbox" 
                    value="Lead Generation" 
                    onChange={handleCheckboxChange} 
                  />
                  <span>Lead Generation</span>
                </label>
                <label htmlFor="notsureyet">
                  <input 
                    id='notsureyet' 
                    name="enquiry-type[]" 
                    type="checkbox" 
                    value="Not sure yet" 
                    onChange={handleCheckboxChange} 
                  />
                  <span>Not sure yet</span>
                </label>
              </div>
              {showEmptyError && formState['enquiry-type'].length === 0 && <p className='error'>Filed is required.</p>}
            </div>
          </div>
          <ReCAPTCHA
            size="invisible"
            ref={recaptchaRef}
            sitekey={captchaKey}
            onChange={handleCaptchaChange}
          />
          <div className="col-lg-12">
            <div className="submit_wraper">
              <input className='cmn_btn m-0' type="submit" value='Submit' />
              {isLoading && (
                <div className="spinner-border mx-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    );
  }


export default function IndustriesForm(initialIndustriesListing:any) {
  

    const letsTalksection = initialIndustriesListing?.initialIndustriesListing?.industriesListinPageSetting?.letsTalkSection;
  return (
    <div className="row">
        <div className="col-lg-4">
        <div className="sc_box">
        {letsTalksection?.letsTalkLink?.url && (
            <Link className='mb-3' href={letsTalksection?.letsTalkLink?.url}>{letsTalksection?.letsTalkLink?.title}</Link>
        )}
            <h2>{letsTalksection?.title}</h2>
            <div className="ceo_wrap">
            <div className="sc_img">
                <Image src={letsTalksection?.ceoImage?.sourceUrl || '/images/placeholder.jpg'} width={70} height={70} alt={letsTalksection?.ceoName || 'ceo'}/>
            </div>
            <span>{letsTalksection?.ceoName}</span>
            </div>
            <h3 className='f24'>{letsTalksection?.subTitle}</h3>
            <p>{letsTalksection?.desctiption}</p>
            {letsTalksection?.scheduleACall?.url && (

            <Link className='cmn_btn_fill' href={`${letsTalksection?.scheduleACall?.url}`}>{letsTalksection?.scheduleACall?.title}</Link>
            )}
        </div>
        </div>
        <div className="col-lg-7 offset-xl-1">
        <div className="sc_form_wraper">
            <Cf7FormWrapper formId="1137">
            <Form />
            </Cf7FormWrapper>
        </div>
        </div>
    </div>
  )
}
